import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';

import { useCerrarConAtras } from '#components/mobile/ui/atras';
import { Icono } from '#components/mobile/ui/Icono';
import { color, radio, sombra } from '#components/mobile/ui/tokens';

import { EtiquetaPermiso } from './EtiquetaPermiso';
import type { Permiso } from './EtiquetaPermiso';
import { InicialAutor } from './InicialAutor';
import type { Tripulante } from './tripulacionLogica';

type FilaTripulanteProps = {
  tripulante: Tripulante;
  /** El nombre tal cual o «Tú · Vadik». */
  nombre: string;
  tipo: Permiso | 'mando';
  /** Solo quien lleva el mando ve el menú «…» de las demás personas. */
  menu?: {
    ocupado: boolean;
    onCambiarPermiso: () => void;
    onBajar: () => void;
  };
};

/**
 * Una persona a bordo en una línea: inicial, nombre, etiqueta y, para quien
 * lleva el mando, «…» con «Cambiar permiso» y «Bajar del barco». El correo
 * no sale aquí (solo en la confirmación de bajar).
 */
export function FilaTripulante({
  tripulante,
  nombre,
  tipo,
  menu,
}: FilaTripulanteProps) {
  const { t } = useTranslation();
  const [abierto, setAbierto] = useState(false);
  // La hoja de confirmar se abre cuando el «atrás» del menú ya se ha
  // deshecho; si no, ese «atrás» la cerraba nada más abrirse.
  const cerrarYLuego = useCerrarConAtras(abierto, () => setAbierto(false));
  const otro = tipo === 'ver' ? t('View and edit') : t('View only');
  const etiquetaMenu = t('Options for {{name}}', { name: tripulante.nombre });

  return (
    <View
      data-testid="fila-tripulante"
      style={{
        position: 'relative',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        padding: '10px 6px 10px 12px',
        minHeight: 64,
      }}
    >
      <View style={{ position: 'relative', flexShrink: 0 }}>
        <InicialAutor tripulante={tripulante} size={40} />
        {tipo === 'mando' && (
          <View
            aria-hidden
            style={{
              position: 'absolute',
              right: -4,
              bottom: -4,
              width: 18,
              height: 18,
              borderRadius: '50%',
              backgroundColor: color.surface,
              border: `1px solid ${color.line2}`,
              color: color.accent,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icono nombre="anchor" size={11} strokeWidth={2.6} />
          </View>
        )}
      </View>
      <Text
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: 15,
          fontWeight: 800,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {nombre}
      </Text>
      <EtiquetaPermiso tipo={tipo} />
      {menu ? (
        <Button
          variant="bare"
          data-testid="menu-tripulante"
          aria-label={etiquetaMenu}
          aria-expanded={abierto}
          isDisabled={menu.ocupado}
          onPress={() => setAbierto(a => !a)}
          style={{
            width: 44,
            height: 44,
            borderRadius: 22,
            flexShrink: 0,
            color: color.fg2,
            backgroundColor: abierto ? color.surface2 : undefined,
          }}
        >
          <Icono nombre="more" size={22} />
        </Button>
      ) : (
        <View style={{ width: 6, flexShrink: 0 }} />
      )}
      {menu && abierto && (
        <>
          {/* Tocar fuera del menú lo cierra. */}
          <View
            aria-hidden
            onClick={() => setAbierto(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 5 }}
          />
          <View
            role="group"
            aria-label={etiquetaMenu}
            onKeyDown={e => e.key === 'Escape' && setAbierto(false)}
            style={{
              position: 'absolute',
              right: 8,
              top: 52,
              zIndex: 6,
              minWidth: 230,
              padding: 6,
              borderRadius: radio.boton,
              backgroundColor: color.surfaceHoja,
              border: `1px solid ${color.line2}`,
              boxShadow: sombra.flotante,
            }}
          >
            <Button
              variant="bare"
              data-testid="cambiar-permiso"
              autoFocus
              onPress={() => cerrarYLuego(menu.onCambiarPermiso)}
              style={estiloOpcion}
            >
              <Text style={{ fontWeight: 700, fontSize: 14.5 }}>
                <Trans>Change permission</Trans>
              </Text>
              <Text
                style={{ fontSize: 12.5, fontWeight: 500, color: color.fg2 }}
              >
                {t('Switch to «{{permission}}»', { permission: otro })}
              </Text>
            </Button>
            <Button
              variant="bare"
              data-testid="bajar-del-barco"
              onPress={() => cerrarYLuego(menu.onBajar)}
              style={{ ...estiloOpcion, color: color.bad }}
            >
              <Text style={{ fontWeight: 700, fontSize: 14.5 }}>
                <Trans>Take off the boat</Trans>
              </Text>
            </Button>
          </View>
        </>
      )}
    </View>
  );
}

const estiloOpcion = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'center',
  gap: 1,
  minHeight: 48,
  padding: '10px 12px',
  borderRadius: 10,
  textAlign: 'left',
} as const;
